// Words and view state for HR → Employee → Compensation. The server decides what is allowed; this only presents it.

export const PAY_FREQUENCIES = [
  { value: "monthly", label: "Monthly", per: "per month" },
  { value: "semi_monthly", label: "Semi-monthly", per: "per cutoff" },
  { value: "bi_weekly", label: "Every two weeks", per: "per two weeks" },
  { value: "weekly", label: "Weekly", per: "per week" },
  { value: "daily", label: "Daily", per: "per day" },
  { value: "hourly", label: "Hourly", per: "per hour" },
];

/** "per day", "per hour", "per cutoff" … for a salary frequency. */
export function payPer(frequency) {
  return PAY_FREQUENCIES.find((item) => item.value === frequency)?.per || "";
}

export function formatPeso(cents) {
  const amount = Number(cents || 0) / 100;
  return `₱${amount.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "₱600.00 per day" */
export function formatPay(cents, frequency) {
  return `${formatPeso(cents)} ${payPer(frequency)}`.trim();
}

// A fixed allowance belongs to salaried pay only; daily and hourly pay has none. The server enforces this.
export const FIXED_ALLOWANCE_FREQUENCIES = ["monthly", "semi_monthly", "bi_weekly", "weekly"];
export const NO_FIXED_ALLOWANCE_TEXT = "Daily and hourly pay has no fixed allowance.";

export function supportsFixedAllowance(frequency) {
  return FIXED_ALLOWANCE_FREQUENCIES.includes(frequency);
}

/**
 * The allowance to send for a typed amount: always 0 for daily / hourly pay, whatever is still in the field, so a
 * value typed for a salaried frequency is never submitted after switching.
 */
export function allowanceCentsFor(input, frequency) {
  if (!supportsFixedAllowance(frequency)) return { cents: 0, error: null };
  const text = String(input ?? "").trim();
  if (text === "") return { cents: 0, error: null };
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return { cents: null, error: "Enter the fixed allowance as an amount of ₱0 or more." };
  return { cents: Math.round(Number(text) * 100), error: null };
}

/** A preset's allowance as a starting value in centavos, or null when there is none to offer. */
export function presetAllowancePrefill(presetAllowanceCents, frequency) {
  const cents = Number(presetAllowanceCents);
  return supportsFixedAllowance(frequency) && Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/** The pay staged for a candidate, in words: "₱20,000.00 per month + ₱3,000.00 per month fixed allowance". */
export function stagedPayText(setup) {
  if (!setup || setup.baseSalaryCents == null) return null;
  const base = formatPay(setup.baseSalaryCents, setup.salaryFrequency);
  const allowance = Number(setup.allowanceCents);
  return supportsFixedAllowance(setup.salaryFrequency) && allowance > 0
    ? `${base} + ${formatPay(allowance, setup.salaryFrequency)} fixed allowance`
    : base;
}

export function dateKey(value) {
  return value ? String(value).slice(0, 10) : "";
}

export function formatPayDate(value) {
  const key = dateKey(value);
  if (!key) return "";
  const parsed = new Date(`${key}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? key
    : parsed.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

// GET /api/business/hr/compensation/:memberId answers { success, records, today, nextChangeDate }.
export function compensationHistoryFrom(response) {
  const records = Array.isArray(response?.records)
    ? response.records
    : Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
  return {
    records,
    today: response?.today || null,
    nextChangeDate: response?.nextChangeDate || null,
  };
}

/** Records grouped by where they stand today: the current one, upcoming changes (soonest first), earlier ones. */
export function groupCompensation(records = []) {
  const stateOf = (record) => record.state || "current";
  const byStart = (a, b) => dateKey(a.effective_from).localeCompare(dateKey(b.effective_from));
  return {
    current: records.find((record) => stateOf(record) === "current") || null,
    scheduled: records.filter((record) => stateOf(record) === "scheduled").sort(byStart),
    past: records.filter((record) => stateOf(record) === "past").sort((a, b) => byStart(b, a)),
  };
}

/** The dates a record covers, in words. */
export function payPeriodText(record) {
  const from = formatPayDate(record.effective_from);
  if (record.state === "scheduled") {
    return record.effective_until ? `Starts ${from}, until ${formatPayDate(record.effective_until)}` : `Starts ${from}`;
  }
  if (record.effective_until) return `${from} – ${formatPayDate(record.effective_until)}`;
  return `Since ${from}`;
}

/**
 * What HR may do with a record. Initial pay is corrected, never cancelled; a record payroll has used is kept as paid.
 */
export function compensationActions(record, canManage) {
  if (!canManage || !record) return { correct: false, cancel: false, lockedReason: null };
  const used = Boolean(record.used);
  return {
    correct: record.can_correct !== false && !used,
    cancel: Boolean(record.can_cancel) && !used,
    lockedReason: used
      ? "Used in a payroll that is with Finance or already paid, so it is kept as it was paid. To pay a different amount, use Change pay."
      : null,
  };
}

/** The message to show for a refused save, and a date the server suggests instead (if any). */
export function compensationError(error) {
  return {
    message: error?.serverMessage || error?.message || "The pay could not be saved.",
    suggestedDate: error?.serverCode === "COMPENSATION_CHANGE_NOT_ON_PERIOD_START"
      ? error?.serverDetails?.suggestedEffectiveFrom || null
      : null,
  };
}
